import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  meetingRequiresAuthentication,
  productHasPrice,
} from "@/lib/productHasPrice";
import { MeetingFormatEnum, resolveMeetingFormat } from "@/types/MeetingFormat";
import { fetchLeadSession, logoutLeadSession } from "../leadSessionClient";
import type { MeetingLeadContact, MeetingProduct } from "./meetingProductTypes";
import {
  getStoredLivestreamGuestName,
  livestreamNeedsGuestNamePrompt,
  setStoredLivestreamGuestName,
} from "./livestreamGuestName";
import { shouldDeferLivestreamJoin } from "./livestreamSchedule";

export function useMeetingSession() {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authTokenFromQuery = searchParams.get("authToken");
  const isHostJoinLink = searchParams.get("host") === "1";
  const isAdminJoinLink = searchParams.get("admin") === "1";
  const isStaffJoinLink = isHostJoinLink || isAdminJoinLink;
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [needsGuestName, setNeedsGuestName] = useState(false);
  const [guestNameSubmitting, setGuestNameSubmitting] = useState(false);
  const [productName, setProductName] = useState("");
  const [productImageUrl, setProductImageUrl] = useState<string | undefined>(
    undefined,
  );
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [meetingFormat, setMeetingFormat] = useState<MeetingFormatEnum>(
    MeetingFormatEnum.WEBINAR,
  );
  const [requireAuthentication, setRequireAuthentication] = useState(false);
  const [hasPrice, setHasPrice] = useState(false);
  const [shopProductIds, setShopProductIds] = useState<string[]>([]);
  const [leadContact, setLeadContact] = useState<MeetingLeadContact | undefined>(
    undefined,
  );
  const [loggingOut, setLoggingOut] = useState(false);
  const [supportEmail, setSupportEmail] = useState<string | null>(null);
  const [meetingStart, setMeetingStart] = useState<string | null>(null);
  const [meetingEnd, setMeetingEnd] = useState<string | null>(null);
  const [livestreamScheduleWaiting, setLivestreamScheduleWaiting] = useState(false);
  const [storedParticipantName, setStoredParticipantName] = useState<
    string | undefined
  >(undefined);
  const [loadedMeetingStart, setLoadedMeetingStart] = useState<string | null>(
    null,
  );
  const [loadedMeetingEnd, setLoadedMeetingEnd] = useState<string | null>(null);
  const [loadedMeetingFormat, setLoadedMeetingFormat] = useState<MeetingFormatEnum>(
    MeetingFormatEnum.WEBINAR,
  );

  const joinMeeting = useCallback(
    async ({
      participantName,
    }: {
      participantName?: string;
    }): Promise<boolean> => {
      if (!productId) return false;

      const joinRes = await fetch(`/api/products/${productId}/meeting/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(participantName ? { name: participantName } : {}),
      });

      const joinData = (await joinRes.json()) as { authToken?: string; error?: string };

      if (!joinRes.ok || !joinData.authToken) {
        if (joinRes.status === 401) {
          setNeedsAuth(true);
          return false;
        }
        setError(joinData.error ?? "Failed to join meeting");
        return false;
      }

      setAuthToken(joinData.authToken);
      return true;
    },
    [productId],
  );

  useEffect(() => {
    if (!productId) return;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setError(null);
      setAuthToken(null);
      setMeetingId(null);
      setNeedsAuth(false);
      setNeedsGuestName(false);
      setRequireAuthentication(false);
      setHasPrice(false);
      setShopProductIds([]);
      setLeadContact(undefined);
      setMeetingFormat(MeetingFormatEnum.WEBINAR);
      setProductImageUrl(undefined);
      setSupportEmail(null);
      setMeetingStart(null);
      setMeetingEnd(null);
      setLivestreamScheduleWaiting(false);
      setStoredParticipantName(undefined);
      setLoadedMeetingStart(null);
      setLoadedMeetingEnd(null);
      setLoadedMeetingFormat(MeetingFormatEnum.WEBINAR);

      try {
        const productRes = await fetch(`/api/products/${productId}`);
        if (!productRes.ok) {
          if (productRes.status === 404) setNotFound(true);
          else setError("Failed to load meeting");
          return;
        }

        const productData = (await productRes.json()) as {
          product?: MeetingProduct;
          supportEmail?: string;
        };
        const product = productData.product;
        setSupportEmail(productData.supportEmail?.trim() || null);

        if (!product?.meeting?.realtimeKit?.meetingId) {
          setError("Meeting is not configured for this product");
          return;
        }

        const resolvedFormat = resolveMeetingFormat(product.meeting.meetingFormat);
        const explicitRequireAuth = product.meeting.requireAuthentication === true;

        setProductName(product.name?.trim() ?? "");
        setProductImageUrl(product.images?.[0]?.url);
        setMeetingId(product.meeting.realtimeKit.meetingId);
        setMeetingFormat(resolvedFormat);
        const start = product.meeting.start ?? null;
        const end = product.meeting.end ?? null;
        setMeetingStart(start);
        setMeetingEnd(end);
        setLoadedMeetingStart(start);
        setLoadedMeetingEnd(end);
        setLoadedMeetingFormat(resolvedFormat);
        setShopProductIds(
          (product.meeting.shop?.productIds ?? []).filter(
            (id): id is string => typeof id === "string" && id.trim().length > 0,
          ),
        );

        const requiresAuth = meetingRequiresAuthentication(product);
        const priced = productHasPrice(product);
        setRequireAuthentication(requiresAuth);
        setHasPrice(priced);

        if (authTokenFromQuery && (!requiresAuth || isStaffJoinLink)) {
          setAuthToken(authTokenFromQuery);
          return;
        }

        let participantName: string | undefined;

        if (requiresAuth) {
          const authRes = await fetchLeadSession({ productId });
          const authData = (await authRes.json()) as {
            authenticated?: boolean;
            name?: string;
            email?: string;
            phoneNumber?: string;
          };
          if (!authData.authenticated) {
            setNeedsAuth(true);
            return;
          }
          participantName = authData.name?.trim() || undefined;
          const email = authData.email?.trim();
          const phone = authData.phoneNumber?.trim();
          if (email || phone) {
            setLeadContact({
              ...(email ? { email } : {}),
              ...(phone ? { phone } : {}),
            });
          }
        }

        const storedGuestName = getStoredLivestreamGuestName({ productId });
        if (!participantName?.trim() && storedGuestName) {
          participantName = storedGuestName;
        }

        if (
          livestreamNeedsGuestNamePrompt({
            meetingFormat: resolvedFormat,
            requireAuthentication: explicitRequireAuth,
            isStaffJoinLink,
            hasAuthTokenFromQuery: Boolean(authTokenFromQuery),
            participantName,
          })
        ) {
          setNeedsGuestName(true);
          return;
        }

        setStoredParticipantName(participantName);

        if (
          shouldDeferLivestreamJoin({
            meetingFormat: resolvedFormat,
            isStaffJoinLink,
            start: product.meeting.start,
            end: product.meeting.end,
            now: new Date(),
          })
        ) {
          setLivestreamScheduleWaiting(true);
          return;
        }

        const joined = await joinMeeting({ participantName });
        if (!joined) return;
      } catch (e) {
        console.error("Meeting load error:", e);
        setError("Failed to load meeting");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [productId, authTokenFromQuery, isStaffJoinLink, joinMeeting]);

  const submitGuestName = useCallback(
    async ({ name }: { name: string }) => {
      if (!productId || guestNameSubmitting) return;

      const trimmed = name.trim();
      if (!trimmed) return;

      setGuestNameSubmitting(true);
      setError(null);
      setStoredLivestreamGuestName({ productId, name: trimmed });

      try {
        setStoredParticipantName(trimmed);

        if (
          shouldDeferLivestreamJoin({
            meetingFormat: loadedMeetingFormat,
            isStaffJoinLink,
            start: loadedMeetingStart,
            end: loadedMeetingEnd,
            now: new Date(),
          })
        ) {
          setLivestreamScheduleWaiting(true);
          setNeedsGuestName(false);
          return;
        }

        const joined = await joinMeeting({ participantName: trimmed });
        if (joined) {
          setNeedsGuestName(false);
        }
      } catch (e) {
        console.error("Livestream guest name join error:", e);
        setError("Failed to join livestream");
      } finally {
        setGuestNameSubmitting(false);
      }
    },
    [
      guestNameSubmitting,
      isStaffJoinLink,
      joinMeeting,
      loadedMeetingEnd,
      loadedMeetingFormat,
      loadedMeetingStart,
      productId,
    ],
  );

  async function handleLogout() {
    if (!productId || loggingOut) return;

    setLoggingOut(true);
    try {
      await logoutLeadSession();
      navigate(`/meetings/${productId}/login`, { replace: true });
    } catch (e) {
      console.error("Meeting logout error:", e);
    } finally {
      setLoggingOut(false);
    }
  }

  const pageTitle = productName.trim() ? `${productName.trim()} · Meeting` : "Meeting";
  const guestNamePageTitle = productName.trim()
    ? `${productName.trim()} · Join livestream`
    : "Join livestream";
  const joinWhenScheduleAllows = useCallback(async () => {
    if (!productId || authToken) return false;

    const joined = await joinMeeting({
      participantName: storedParticipantName,
    });
    if (joined) {
      setLivestreamScheduleWaiting(false);
    }
    return joined;
  }, [authToken, joinMeeting, productId, storedParticipantName]);

  const showLogout = requireAuthentication && !isStaffJoinLink;
  const ready = Boolean(authToken && meetingId && !error);

  return {
    productId,
    loading,
    notFound,
    error,
    needsAuth,
    needsGuestName,
    guestNameSubmitting,
    submitGuestName,
    productName,
    productImageUrl,
    pageTitle,
    guestNamePageTitle,
    authToken,
    meetingId,
    meetingFormat,
    shopProductIds,
    leadContact,
    showLogout,
    loggingOut,
    handleLogout,
    ready,
    hasPrice,
    supportEmail,
    meetingStart,
    meetingEnd,
    isHostJoinLink,
    isAdminJoinLink,
    livestreamScheduleWaiting,
    joinWhenScheduleAllows,
  };
}
